import { getDocsConfig } from "@/features/branding";
import { CmsPostCategoryId, getCmsPost, getCmsPostByIdAndCategory } from "@/features/cms";
import { CmsType } from "@/features/cms/types";
import { CmsPost } from "@/store/services/internal";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, ButtonProps, Drawer } from "antd";
import classnames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import styles from "./ContextualInfoButton.module.scss";
export interface ContextualInfoButtonProps extends Omit<ButtonProps, "content"> {
  id: string;
  category?: CmsPostCategoryId;
  cmsType?: CmsType;
  content?: {
    title: string;
    body: string;
  };
  drawerWidth?: number;
}
const useLoadContent = (id: string, category: CmsPostCategoryId, cmsType: CmsType, content?: {
  title: string;
  body: string;
}) => {
  const [post, setPost] = useState<CmsPost | undefined>(undefined);
  useEffect(() => {
    // We don't load the data if tutorials are disabled.
    if (getDocsConfig().contextualElementTutorials.enabled || !content) {
      if (cmsType === "article") {
        getCmsPost(category, id).then(setPost);
      } else if (cmsType === "category") {
        getCmsPostByIdAndCategory(id, category).then(setPost);
      }
    }
  }, [id, category, content]);
  if (content) {
    return content;
  }
  if (!post) {
    return {
      title: "",
      body: ""
    };
  }
  return {
    title: post.title,
    body: post.body && post.body !== "null" ? post.body : "<i>Unfortunately, this page has not yet been written.</i>"
  };
};

/**
 * Shows a button in-context which will open a tutorial drawer. Visible only
 * when a tutorial is available.
 */
export const ContextualInfoButton = ({
  id,
  category = "glossary",
  children,
  content: givenContent,
  cmsType = "article",
  className,
  drawerWidth = 600,
  ...props
}: ContextualInfoButtonProps) => {
  const [open, setOpen] = useState(false);
  const content = useLoadContent(id, category, cmsType, givenContent);
  const showDrawer = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };
  const stopMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // We don't show this button if the information hasn't loaded.
  if (!content.title && !content.body) {
    return <></>;
  }
  return <>
      <Button className={classnames(styles.button, className)} onClick={showDrawer} onMouseDown={stopMouseDown} type="text" {...props} data-sentry-element="Button" data-sentry-source-file="ContextualInfoButton.tsx">
        {children || <InfoCircleOutlined />}
      </Button>
      <Drawer title={content.title} open={open} onClose={() => setOpen(false)} placement="right" size={drawerWidth} className={styles.drawer} data-sentry-element="Drawer" data-sentry-source-file="ContextualInfoButton.tsx">
        <div className="cms prose" dangerouslySetInnerHTML={{
        __html: content.body
      }} style={{
        wordWrap: "break-word"
      }} />
      </Drawer>
    </>;
};
export interface ContextualInfoButtonWithTextProps extends ContextualInfoButtonProps {
  rootClassName?: string;
  children: ReactNode;
  justify?: "between";
  drawerWidth?: number;
}
export const ContextualInfoButtonWithText = ({
  children,
  rootClassName,
  justify,
  drawerWidth,
  ...props
}: ContextualInfoButtonWithTextProps) => {
  const stopEvent = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return (
    // eslint-disable-next-line
    <span className={classnames(rootClassName, "inline-flex w-full items-center gap-1", justify === "between" && "justify-between")} onClick={stopEvent} onMouseDown={stopEvent} data-sentry-component="ContextualInfoButtonWithText" data-sentry-source-file="ContextualInfoButton.tsx">
      <span>{children}</span>
      <ContextualInfoButton {...props} drawerWidth={drawerWidth} data-sentry-element="ContextualInfoButton" data-sentry-source-file="ContextualInfoButton.tsx" />
    </span>
  );
};